import { useQConfig } from '@exam-management/feature-qconfig-provider';
import { QConfigStatus } from '@exam-management/graphql';
import {
  Collapse,
  DeleteOutlineIcon,
  ExpandMoreRoundedIcon,
  Flex,
  IconButton,
  Text,
  MoreMenuIconButton,
  Rotate,
  type FlexProps,
  type MoreMenuIconButtonOption,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useHasPermission } from '@shared/permissions';

import { StructureNameInput } from '../../../components';
import { EDIT_QCONFIG_REQUIRED_PERMISSIONS } from '../../../constants';
import { useQConfigStructureModal } from '../../../providers';
import type { QConfigStructureData } from '../../../types';

export interface PartSectionProps extends FlexProps {
  partData: QConfigStructureData;
}

export function PartSection(props: Readonly<PartSectionProps>) {
  const { partData, children, ...restProps } = props;
  const { qConfigData } = useQConfig();
  const isDraftQConfig = qConfigData.status === QConfigStatus.Draft;
  const hasPermissionForEditingQConfig = useHasPermission(EDIT_QCONFIG_REQUIRED_PERMISSIONS);
  const [isOpen, { toggle }] = useDisclosure(true);
  const { setAction } = useQConfigStructureModal();
  const partTotalQuestions = partData.totalQuestions;

  const handleRemovePart = () => {
    setAction({
      action: 'QCONFIG_DELETE',
      payload: { qConfigStructureId: partData.id },
    });
  };

  const morePartOptions: MoreMenuIconButtonOption[] = [
    {
      icon: (
        <DeleteOutlineIcon
          sx={(theme) => ({
            color: theme.palette.error.main,
          })}
        />
      ),
      label: 'Remove',
      onClick: handleRemovePart,
      sx: (theme) => ({
        ':hover': {
          backgroundColor: theme.palette.primary.lighter,
        },
        color: theme.palette.error.main,
      }),
    },
  ];

  return (
    <Flex
      {...restProps}
      sx={{
        flexDirection: 'column',
        ...restProps?.sx,
      }}
    >
      <Flex
        sx={(theme) => ({
          alignItems: 'center',
          py: theme.spacing(2.5),
          pl: theme.spacing(3),
          pr: theme.spacing(4),
          borderRadius: theme.shape.rounded(2),
          border: '1px solid',
          borderColor: (theme) => (isOpen ? theme.palette.primary.main : theme.palette.grey[300]),
          bgcolor: (theme) =>
            isOpen ? theme.palette.primary.main : theme.palette.background.paper,
        })}
      >
        <IconButton onClick={toggle}>
          <Rotate in={isOpen}>
            <ExpandMoreRoundedIcon />
          </Rotate>
        </IconButton>
        <Flex
          sx={(theme) => ({
            flex: 1,
            gap: theme.spacing(2),
          })}
        >
          <StructureNameInput
            name={partData.id}
            variant='h5'
            slotProps={{
              editIcon: { sx: { fontSize: '24px' } },
            }}
            disabled={!hasPermissionForEditingQConfig || !isDraftQConfig}
          />
        </Flex>
        <Flex
          sx={(theme) => ({
            gap: theme.spacing(2),
            p: theme.spacing(0.5, 2, 0.5, 6),
            alignItems: 'center',
          })}
        >
          <Text variant='h5'>จำนวน</Text>
          <Text variant='h5'>{partTotalQuestions}</Text>
          <Text variant='h5'>ข้อ</Text>
        </Flex>
        {isDraftQConfig && hasPermissionForEditingQConfig ? (
          <MoreMenuIconButton options={morePartOptions} />
        ) : null}
      </Flex>
      <Collapse in={isOpen}>{children}</Collapse>
    </Flex>
  );
}
